/**
 * ApplyBusinessV2.js — Standalone shell for Business Single Application V2.
 *
 * Handles Business Plus Onboarding (type=1), Business Prime Onboarding (type=2),
 * and Business Prime Upgrade (type=3) in one unified, JSON-driven flow.
 *
 * ARCHITECTURAL DECISION:
 * This is a fully standalone component. It does NOT delegate to ApplyBusinessPeriodicReview.
 * The periodic review was used as a structural reference only. The logic for V2 onboarding
 * and periodic review will diverge in the future, so coupling them would create maintenance risk.
 */

import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AutoSaveIndicator from '../../../../components/AutoSaveIndicator/AutoSaveIndicator';
import { createApplicationFormUpdateHandler } from '../../../../components/ApplicationInvite';
import Stepper from '../../../../components/Common/Tab/Stepper';
import ReviewShellBoundary from '../../../../components/ReviewWizard/ReviewShellBoundary';
import CountrySelectService from '../../../../components/CSC/CountrySelect/CountrySelectService';
import { setValue } from '../../../../components/InputField/helper';
import useAutoSave from '../../../../hooks/useAutoSave';
import useFormReviewShell from '../../../../hooks/useFormReviewShell';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import { DialogContext } from '../../../../store/context/DialogContext';
import { useTranslation } from '../../../../store/context/TranslationContext';
import { doGET, doPUT } from '../../../../util/HttpUtil';
import { handleUtmFormSubmit } from '../../../../util/utm';
import { withComputedHidePinCode } from '../../components/PincodeField/helper';
import pincodeService from '../../components/PincodeField/PincodeService';
import ConsoleAccordion from '../BPrime/components/ConsoleAccordion';
import { AskMoreMessage, ThankYou } from '../components';
import ApplicationDocRedo from '../components/IndividualLite/ApplicationDocRedo';
import ExtraQuestions from '../ExtraQuestions';
import { hasActiveAsks, hasPendingAsks } from '../askIdentity';
import { CONVERSIONS_FIELD_MAP } from '../IndividualPlus/constant';
import {
  checkIsAskForInfo, convertArrayToDict, FORM_TYPE,
  scrollToTop, STEP_STATUS, STEPS
} from '../IPlushelper';
import "../styles.scss";
import { buildPostAutoSaveUpdatePayload, getAutoSaveProceedBlockedMessage, isAutoSaveEnabledForConfig, resolveAutoSaveDebounceMs } from '../autosave/shared';
import { createBusinessV2AutoSaveAdapter } from './autosaveV2';
import { hydrateBusinessV2PayloadFromApi, normalizeBusinessV2PayloadForApi, unsetHiddenBusinessV2Fields } from './compatibilityV2';
import {
  calculateInitialErrors,
  clearErrorsForFields,
  createFieldDefinitionMap,
  fetchBusinessDetailData, getNextStepV2, handlePrevV2, isApplicationLocked,
  getEntryContextIntroWizard,
  isTypeSwitchAllowed, cleanupFieldsForTypeSwitch, movePrimaryUserToFront,
  normalizeWizardSequenceForType, resolveActiveWizards, resolveFieldsForType, V2_TYPE, V2_TYPE_LABELS,
  BUSINESS_INFORMATION_WIZARDS,
  getSponsoredHiddenWizards,
  WIZARD, WIZARD_NUM,
} from './helperV2';
import { runRecordValidators } from './recordValidators';

import BusinessInformation from "./BusinessInformationV2";
import AssetsInvolvement from "./AssetsInvolvementV2";
import BusinessActivity from "./BusinessActivityV2";
import BusinessType from "./BusinessTypeV2";
import ComplianceDetails from "./ComplianceDetailsV2";
import FundFamilyOffices from "./FundFamilyOfficesV2";
import MoneyServicesBusiness from "./MoneyServicesBusinessV2";
import SelfDeclaration from "./SelfDeclarationV2";
import FormContainer, { FORM_CONTAINER_TYPE } from './FormContainerV2';
import DocumentsV2 from './DocumentsV2';
import DocumentsVersion2 from './DocumentsVersion2';
import BusinessInformationVersion2 from './BusinessInformationVersion2';
import DocumentFirstChrome from './components/DocumentFirstChrome';
import { isDocumentFirst } from './documentFirst';
import { clampWizardToDocumentGate } from './documentFirstNavGuard';
import { VARIANT } from '../../../../util/variant';
import PaymentStep from './PaymentStep';
import AccountActivityV2 from './AccountActivityV2';
import BusinessPlusIntroductionV2 from './BusinessPlusIntroductionV2';
import PrimeRequiredModalV2 from './PrimeRequiredModalV2';


// ── Component Map ──
const COMPONENTS = {
  BusinessPlusIntroduction: BusinessPlusIntroductionV2,
  BusinessInformation,
  [WIZARD.BusinessIdentity]: BusinessInformationVersion2,
  [WIZARD.ContactAndAddresses]: BusinessInformationVersion2,
  [WIZARD.PeopleAndOwnership]: BusinessInformationVersion2,
  BusinessActivity,
  SelfDeclaration,
  BusinessType,
  AccountActivity: AccountActivityV2,
  MoneyServicesBusiness,
  AssetsInvolvement,
  FundFamilyOffices,
  ComplianceDetails,
  Documents: DocumentsV2,
  PayNow: PaymentStep,
  ExtraQuestions,
  ApplicationDocRedo,
  ThankYou,
};

const DOCUMENT_FIRST_FIELDS_GROUP = "BUSINESS_V2_0";
const DOCUMENT_FIRST_FORM_ID = "business_v2";
const DOC_FIRST_THEMED_WIZARDS = [WIZARD.AccountActivity, WIZARD.BusinessActivity, WIZARD.SelfDeclaration];
const DEFERRED_STEP_WINDOW_MS = 15000;

const resolveStepComponent = (wizardKey, context) => {
  if (!isDocumentFirst(context)) return COMPONENTS[wizardKey];
  if (wizardKey === WIZARD.Documents) return DocumentsVersion2;
  if (BUSINESS_INFORMATION_WIZARDS.includes(wizardKey)) return BusinessInformationVersion2;
  return COMPONENTS[wizardKey];
};

const ACCORDION_LABELS = {
  BusinessPlusIntroduction: 'Introduction',
  [WIZARD.BusinessInformation]: 'Applicant Information',
  [WIZARD.BusinessIdentity]: 'Business identity',
  [WIZARD.ContactAndAddresses]: 'Contact and addresses',
  [WIZARD.PeopleAndOwnership]: 'People and ownership',
  [WIZARD.BusinessActivity]: 'Business Activity and Account Usage',
  [WIZARD.SelfDeclaration]: 'Terms & Conditions',
  [WIZARD.BusinessType]: 'Business Types',
  [WIZARD.AccountActivity]: 'Business Activity and Account Usage',
  [WIZARD.MoneyServicesBusiness]: 'Money Services Business',
  [WIZARD.AssetsInvolvement]: 'Cryptocurrency and Digital Assets Involvement',
  [WIZARD.FundFamilyOffices]: 'Fund & Family Offices',
  [WIZARD.ComplianceDetails]: 'Compliance Details',
  [WIZARD.Documents]: 'Documents',
  [WIZARD.ExtraQuestions]: 'Requested Information',
};
const FORM_TITLES = {
  [V2_TYPE.PLUS]: "Business Plus Account Application",
  [V2_TYPE.PRIME]: "Business Prime Account Application",
  [V2_TYPE.UPGRADE]: "Business Prime Upgrade Application",
};

const buildLocalId = () =>
  `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const BUSINESS_V2_PRESERVED_ROOT_KEYS = [
  '_id',
  'email',
  'status',
  'seq',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'entityCombinedStatus',
  'statusUpdatedBy',
  'statusUpdatedAt',
  'applicationNo',
  'connectedApplication',
  'formType',
  'brokerId',
  'brokerReferenceId',
  'createdByBrokerAdminId',
  'paymentPolicySnapshot',
  'brokerConsentAttestation',
];

const preserveBusinessV2RootMeta = (previousData, nextData, applicationForm = null) => {
  if (!_.isPlainObject(nextData) || Array.isArray(nextData)) {
    return nextData;
  }

  const preservedData = { ...nextData };
  const previousObject = _.isPlainObject(previousData) ? previousData : {};
  const applicationType = Number(applicationForm?.type);

  BUSINESS_V2_PRESERVED_ROOT_KEYS.forEach((key) => {
    if (preservedData[key] === undefined && previousObject[key] !== undefined) {
      preservedData[key] = _.cloneDeep(previousObject[key]);
    }
  });

  if (preservedData.type === undefined) {
    if (Number.isFinite(applicationType) && applicationType > 0) {
      preservedData.type = applicationType;
    } else if (previousObject.type !== undefined) {
      preservedData.type = _.cloneDeep(previousObject.type);
    }
  }

  if (preservedData.interestedInPrime === undefined && previousObject.interestedInPrime !== undefined) {
    preservedData.interestedInPrime = _.cloneDeep(previousObject.interestedInPrime);
  }

  return preservedData;
};

const normalizeWizardForV2 = (wizard) => wizard;

const QUERY_EXCLUDED_WIZARDS = new Set([
  WIZARD.ThankYou,
  WIZARD.ExtraQuestions,
  WIZARD.ApplicationDocRedo,
  WIZARD.AskMoreMessage,
]);
const PLUS_INTRO_WIZARD = "BusinessPlusIntroduction";

const ensureDraftArrayItemIdentity = (items = [], identityFields = ['_id']) => (
  (Array.isArray(items) ? items : []).map((item) => {
    if (!_.isPlainObject(item)) return item;
    const hasStableIdentity = identityFields.some((field) => Boolean(item?.[field]));
    if (hasStableIdentity || item?._localId) return item;
    return { ...item, _isNew: true, _localId: buildLocalId() };
  })
);

const mergeBusinessV2ResponseData = ({
  responseData = {},
  fallbackData = {},
  formType,
  type,
}) => {
  const hydratedResponse = hydrateBusinessV2PayloadFromApi(responseData ?? {});
  const mergedData = buildPostAutoSaveUpdatePayload(hydratedResponse, fallbackData);

  const fallbackIndividuals = Array.isArray(fallbackData?.individuals) ? fallbackData.individuals : [];
  const fallbackEntities = Array.isArray(fallbackData?.entities) ? fallbackData.entities : [];
  const fallbackAddressList = Array.isArray(fallbackData?.addressList) ? fallbackData.addressList : [];

  mergedData.individuals = ensureDraftArrayItemIdentity(
    movePrimaryUserToFront(
      Array.isArray(mergedData?.individuals) && mergedData.individuals.length
        ? mergedData.individuals
        : fallbackIndividuals
    ),
    ['aEId', 'cyclosUserId', 'cyclosUserName', '_id']
  );

  mergedData.entities = mergedData?.hasEntity === false
    ? []
    : ensureDraftArrayItemIdentity(
      Array.isArray(mergedData?.entities)
        ? mergedData.entities
        : fallbackEntities,
      ['aEId', '_id']
    );

  mergedData.addressList = ensureDraftArrayItemIdentity(
    Array.isArray(mergedData?.addressList)
      ? mergedData.addressList
      : fallbackAddressList,
    ['type', '_id']
  );

  return {
    ...mergedData,
    formType,
    type,
  };
};

const resolveWizardNameFromStepParam = (rawStep) => {
  if (rawStep === undefined || rawStep === null) return null;
  const stepValue = String(rawStep).trim();
  if (!stepValue) return null;

  const numeric = Number(stepValue);
  if (!Number.isNaN(numeric)) {
    const wizardKey = Object.keys(WIZARD_NUM || {}).find((key) => WIZARD_NUM[key] === numeric);
    if (wizardKey && WIZARD?.[wizardKey]) return WIZARD[wizardKey];
  }

  const byValue = Object.values(WIZARD || {}).find(
    (value) => String(value).toLowerCase() === stepValue.toLowerCase()
  );
  if (byValue) return byValue;

  const byKey = Object.keys(WIZARD || {}).find(
    (key) => String(key).toLowerCase() === stepValue.toLowerCase()
  );
  if (byKey && WIZARD?.[byKey]) return WIZARD[byKey];

  return null;
};

const getQueryWizardSequence = ({ type, formData, jsonConfig }) => (
  [
    ...(jsonConfig ? resolveActiveWizards(type, formData, jsonConfig) : [])
  ]
    .map((wizard) => normalizeWizardForV2(wizard, type))
    .filter((wizard, index, arr) => !QUERY_EXCLUDED_WIZARDS.has(wizard) && arr.indexOf(wizard) === index)
);

const getFullWizardSequence = ({ type, jsonConfig }) => (
  [
    ...(normalizeWizardSequenceForType(type, jsonConfig?.wizardSequenceByType?.[String(type)] || [])
      .map((wizard) => normalizeWizardForV2(wizard, type))),
  ].filter((wizard, index, arr) => !QUERY_EXCLUDED_WIZARDS.has(wizard) && arr.indexOf(wizard) === index)
);

const getDisplayWizardSequence = ({ type, formData, jsonConfig } = {}) => {
  if (Number(type) === V2_TYPE.PLUS) {
    const fullSequence = getFullWizardSequence({ type, jsonConfig });
    // Plus shows the full sequence so conditional steps stay visible in the stepper. But broker-
    // sponsored (brokerId) apps can NEVER reach the payment or Terms & Conditions steps — navigation
    // (resolveActiveWizards) filters them out and the flow submits on the last field step. Without the
    // same filter here the stepper advertises steps the broker can never reach (e.g. "7 Review",
    // "8 Payment") and the header reads "STEP 6 OF 8" instead of "OF 6". Keep display == navigation.
    if (formData?.brokerId) {
      const hiddenWizards = getSponsoredHiddenWizards(jsonConfig);
      return fullSequence.filter((wizard) => !hiddenWizards.includes(wizard));
    }
    return fullSequence;
  }

  return getQueryWizardSequence({ type, formData, jsonConfig });
};

const getDefaultInitialWizardForType = (type, jsonConfig) => {
  return getEntryContextIntroWizard(type, jsonConfig) || WIZARD.BusinessType;
};

const resolveFallbackWizard = ({ currentWizard, type, formData, jsonConfig }) => {
  const activeSequence = getQueryWizardSequence({ type, formData, jsonConfig });
  if (!activeSequence.length) {
    return getDefaultInitialWizardForType(type, jsonConfig);
  }

  const normalizedWizard = normalizeWizardForV2(currentWizard, type);
  const fullSequence = getFullWizardSequence({ type, jsonConfig });
  const currentIndex = fullSequence.indexOf(normalizedWizard);

  if (currentIndex === -1) {
    return activeSequence[0];
  }

  const activeIndices = activeSequence
    .map((wizard) => ({ wizard, index: fullSequence.indexOf(wizard) }))
    .filter(({ index }) => index >= 0);

  const nextActive = activeIndices.find(({ index }) => index > currentIndex);
  if (nextActive?.wizard) {
    return nextActive.wizard;
  }

  const previousActive = [...activeIndices].reverse().find(({ index }) => index < currentIndex);
  if (previousActive?.wizard) {
    return previousActive.wizard;
  }

  return activeSequence[0];
};

const resolveWizardFromStepParam = (rawStep, { type, formData, jsonConfig } = {}) => {
  const stepValue = String(rawStep ?? '').trim();
  if (!stepValue) return null;

  const wizardSequence = getQueryWizardSequence({ type, formData, jsonConfig });
  if (!wizardSequence.length) return null;

  if (/^\d+$/.test(stepValue)) {
    const ordinal = Number(stepValue);
    if (ordinal >= 1 && ordinal <= wizardSequence.length) {
      return wizardSequence[ordinal - 1];
    }
  }

  const resolvedByName = resolveWizardNameFromStepParam(stepValue);
  if (!resolvedByName) return null;

  const normalizedWizard = normalizeWizardForV2(resolvedByName, type);
  return wizardSequence.includes(normalizedWizard) ? normalizedWizard : null;
};

const getStepParamForWizard = (wizard, { type, formData, jsonConfig } = {}) => {
  const normalizedWizard = normalizeWizardForV2(wizard, type);
  const wizardSequence = getQueryWizardSequence({ type, formData, jsonConfig });
  const wizardIndex = wizardSequence.indexOf(normalizedWizard);
  return wizardIndex >= 0 ? String(wizardIndex + 1) : null;
};

const getDisplayStepNumberForWizard = (wizard, { type, formData, jsonConfig } = {}) => {
  const normalizedWizard = normalizeWizardForV2(wizard, type);
  const wizardSequence = getDisplayWizardSequence({ type, formData, jsonConfig });
  const wizardIndex = wizardSequence.indexOf(normalizedWizard);
  return wizardIndex >= 0 ? wizardIndex + 1 : 0;
};

// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════ COMPONENT ════════════════════════════════

const ApplyBusinessV2 = ({ asLabel, formId, editMode = false, refreshId, brokerMode = false }) => {
  const { edit_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  let { showError, showConfirm } = useContext(DialogContext);
  const { t } = useTranslation();
  const isBrokerMode = brokerMode === true
    || location.pathname.startsWith('/brokers/applications/')
    || location.pathname.startsWith('/platform/applications/');

  // ── State ──
  const [data, setData] = useState({});
  const [applicationForm, setApplicationForm] = useState({});
  const [hasError, setHasError] = useState(false);
  const [editId, setEditID] = useState(edit_id ?? formId);
  const [loading, setLoading] = useState(false);
  const [typeSwitchLoading, setTypeSwitchLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [step, setStep] = useState({ wizard: WIZARD.BusinessType });
  const [errors, setErrors] = useState({});
  const [applicationSteps, setApplicationSteps] = useState({});
  const [cyclosFields, setCyclosFields] = useState({});
  const [iPlusFields, setIPlusFields] = useState([]);
  const [autoSaveConfig, setAutoSaveConfig] = useState(null);
  const [jsonConfig, setJsonConfig] = useState(null); // Full JSON config (wizardSequenceByType, etc.)
  const [primeRequiredModalState, setPrimeRequiredModalState] = useState({
    isOpen: false,
    optionLabel: "",
  });
  const pageNumberRef = useRef(WIZARD_NUM.BusinessInformation);
  const primeRequiredResolverRef = useRef(null);

  // V2-specific state
  const [v2Type, setV2Type] = useState(V2_TYPE.PLUS); // type 1, 2, or 3

  // Derived state
  const isLocked = isApplicationLocked(applicationForm, jsonConfig);
  const isEddApplication = applicationForm?.eDDApplication === true;
  const forcePrimeFirstStepOnEntry = Number(location?.state?.businessV2ForceFirstStep) === 1 || location?.state?.businessV2ForceFirstStep === true;
  const v2TypeLabel = V2_TYPE_LABELS[v2Type] || "Business";
  const formTitle = FORM_TITLES[v2Type] || `${v2TypeLabel} Application`;
  const stepWizard = normalizeWizardForV2(step?.wizard, v2Type);
  // Use the actually-rendered wizard as the review "current wizard". We must NOT remap the
  // intro pseudo-wizard to BusinessInformation here: doing so makes goToTask believe it is
  const reviewWizard = stepWizard;
  const activeWizardSequence = useMemo(
    () => getQueryWizardSequence({ type: v2Type, formData: data, jsonConfig }),
    [data, jsonConfig, v2Type]
  );
  const displayWizardSequence = useMemo(
    () => getDisplayWizardSequence({ type: v2Type, formData: data, jsonConfig }),
    [data, jsonConfig, v2Type]
  );
  const shouldRenderStepper = Number(v2Type) === V2_TYPE.PLUS && displayWizardSequence.length > 1;
  const canonicalStepParam = useMemo(
    () => getStepParamForWizard(stepWizard, { type: v2Type, formData: data, jsonConfig }),
    [data, jsonConfig, stepWizard, v2Type]
  );
  const stepWizardRef = useRef(stepWizard);
  const v2TypeRef = useRef(v2Type);
  const applicationFormRef = useRef(applicationForm);
  const locationSearchRef = useRef(location.search);
  const pendingQueryStepRef = useRef(null);
  const pendingQueryWizardRef = useRef(null);
  const documentGateRef = useRef({ open: false, wizard: null });
  const deferredWizardRef = useRef(null);
  const deferredAtRef = useRef(0);
  const componentApplicationForm = useMemo(() => ({
    ...applicationForm,
    type: v2Type,
    getNextStepForForm: ({ wizard: currentWizard, data: formData }) =>
      getNextStepV2(currentWizard, v2Type, formData || data, jsonConfig),
  }), [applicationForm, data, jsonConfig, v2Type]);

  const documentFirstContext = useMemo(
    () => ({ applicationForm: componentApplicationForm, isBrokerMode, v2Type }),
    [componentApplicationForm, isBrokerMode, v2Type],
  );

  const isDocumentFirstShell = useMemo(
    () => isDocumentFirst(documentFirstContext),
    [documentFirstContext],
  );

  const replaceStepQueryParam = useCallback((nextValue) => {
    const params = new URLSearchParams(location.search);
    const normalizedValue = nextValue ? String(nextValue) : null;
    const currentValue = params.get('step');

    if ((currentValue || null) === normalizedValue) {
      return;
    }

    if (normalizedValue) {
      params.set('step', normalizedValue);
    } else {
      params.delete('step');
    }

    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const navigateToWizard = useCallback((wizard, {
    type = v2TypeRef.current,
    formData = data,
    syncQuery = true,
    shouldScroll = true,
  } = {}) => {
    const normalizedWizard = clampWizardToDocumentGate({
      wizard: normalizeWizardForV2(wizard, type),
      sequence: getQueryWizardSequence({ type, formData, jsonConfig }),
      documentFirst: isDocumentFirst(documentFirstContext),
      gateOpen: documentGateRef.current?.open !== false,
    });

    if (normalizedWizard !== normalizeWizardForV2(wizard, type) && !deferredWizardRef.current) {
      deferredWizardRef.current = normalizeWizardForV2(wizard, type);
      deferredAtRef.current = Date.now();
    }
    const nextStepParam = QUERY_EXCLUDED_WIZARDS.has(normalizedWizard)
      ? null
      : getStepParamForWizard(normalizedWizard, { type, formData, jsonConfig });

    setStep({ wizard: normalizedWizard });

    const nextDisplayStep = getDisplayStepNumberForWizard(normalizedWizard, { type, formData, jsonConfig });
    if (nextDisplayStep > 0) {
      pageNumberRef.current = nextDisplayStep;
    }

    if (syncQuery) {
      pendingQueryWizardRef.current = normalizedWizard;
      pendingQueryStepRef.current = nextStepParam;
      replaceStepQueryParam(nextStepParam);
    } else {
      pendingQueryWizardRef.current = null;
      pendingQueryStepRef.current = null;
    }

    if (shouldScroll) {
      scrollToTop();
    }

    return normalizedWizard;
  }, [data, documentFirstContext, jsonConfig, replaceStepQueryParam]);

  useEffect(() => {
    stepWizardRef.current = stepWizard;
  }, [stepWizard]);

  useEffect(() => {
    v2TypeRef.current = v2Type;
  }, [v2Type]);

  useEffect(() => {
    applicationFormRef.current = applicationForm;
  }, [applicationForm]);

  useEffect(() => {
    locationSearchRef.current = location.search;
  }, [location.search]);

  // ── Review Shell ──
  const {
    reviewError,
    reviewLoading,
    reviewNavigation,
  } = useFormReviewShell({
    applicationId: editId,
    // Brokers manage the application too, so they must see and resolve FV Bank's
    // form-review clarifications (the backend authorizes any logged-in user).
    autoLoad: !asLabel && !editMode && Boolean(editId),
    fieldDefinitions: cyclosFields,
    formData: data,
    currentWizard: reviewWizard,
    // Order review issues by the form's real navigation sequence (a field's
    // wizard order, not its position in cyclosFields), so "Issue 1 of N" tracks
    wizardOrder: getQueryWizardSequence({ type: v2Type, formData: data, jsonConfig }),
    onNavigateToWizard: (wizard) => {
      const normalizedWizard = normalizeWizardForV2(wizard, v2Type);
      navigateToWizard(normalizedWizard, { type: v2Type, formData: data });
    },
    onPersistError: showError,
  });

  // ── Deep-link support via query param ──
  useEffect(() => {
    if (asLabel || editMode || dataLoading || !jsonConfig) {
      return;
    }

    if (!applicationForm?._id) {
      return;
    }

    if (isDocumentFirst(documentFirstContext)
      && jsonConfig?.form !== DOCUMENT_FIRST_FORM_ID) {
      return;
    }

    if (QUERY_EXCLUDED_WIZARDS.has(stepWizard)) {
      if (new URLSearchParams(location.search).has('step')) {
        replaceStepQueryParam(null);
      }
      return;
    }

    if (stepWizard && !activeWizardSequence.includes(stepWizard)) {
      const requestedWizard = resolveWizardFromStepParam(
        new URLSearchParams(location.search).get('step'),
        { type: v2Type, formData: data, jsonConfig },
      );
      const fallbackWizard = (requestedWizard && activeWizardSequence.includes(requestedWizard))
        ? requestedWizard
        : resolveFallbackWizard({
          currentWizard: stepWizard,
          type: v2Type,
          formData: data,
          jsonConfig,
        });
      if (fallbackWizard && fallbackWizard !== stepWizard) {
        navigateToWizard(fallbackWizard, {
          type: v2Type,
          formData: data,
        });
        return;
      }
    }

    const queryStep = new URLSearchParams(location.search).get('step');
    const pendingQueryStep = pendingQueryStepRef.current;
    const pendingQueryWizard = pendingQueryWizardRef.current;

    if (pendingQueryWizard && stepWizard === pendingQueryWizard) {
      if ((queryStep || null) !== (pendingQueryStep || null)) {
        return;
      }

      pendingQueryWizardRef.current = null;
      pendingQueryStepRef.current = null;
    }

    const wizardFromQuery = resolveWizardFromStepParam(queryStep, {
      type: v2Type,
      formData: data,
      jsonConfig,
    });

    if (wizardFromQuery && wizardFromQuery !== stepWizard) {
      const landedWizard = navigateToWizard(wizardFromQuery, {
        type: v2Type,
        formData: data,
        syncQuery: false,
      });
      if (landedWizard && landedWizard !== wizardFromQuery) {
        replaceStepQueryParam(
          QUERY_EXCLUDED_WIZARDS.has(landedWizard)
            ? null
            : getStepParamForWizard(landedWizard, { type: v2Type, formData: data, jsonConfig }),
        );
      }
      return;
    }

    if (canonicalStepParam && queryStep !== canonicalStepParam) {
      replaceStepQueryParam(canonicalStepParam);
      return;
    }

    if (!canonicalStepParam && queryStep) {
      replaceStepQueryParam(null);
    }
  }, [
    asLabel,
    activeWizardSequence,
    canonicalStepParam,
    data,
    dataLoading,
    editMode,
    jsonConfig,
    location.search,
    navigateToWizard,
    replaceStepQueryParam,
    stepWizard,
    v2Type,
  ]);

  // ── Error Calculation ──
  useEffect(() => {
    calculateInitialErrors({
      data,
      step: stepWizard ? { wizard: stepWizard } : null,
      iPlusFields,
      setErrors,
      errors: {},
      type: v2Type,
    });
  }, [data, iPlusFields, stepWizard, v2Type]);

  // ── Data Change Handler ──
  const handleChange = useCallback((value) => {
    let clearedFieldPaths = [];

    setData((previousData) => {
      const nextData = typeof value === "function" ? value(previousData) : value;

      if (!_.isPlainObject(nextData) && !Array.isArray(nextData)) {
        return nextData;
      }

      const normalizedNextData = nextData === previousData ? _.cloneDeep(nextData) : nextData;
      const preservedNextData = preserveBusinessV2RootMeta(previousData, normalizedNextData, applicationFormRef.current);
      const sanitizedResult = unsetHiddenBusinessV2Fields(preservedNextData, cyclosFields);
      clearedFieldPaths = sanitizedResult.clearedFieldPaths;
      return sanitizedResult.payload;
    });

    if (clearedFieldPaths.length) {
      clearErrorsForFields({
        setErrors,
        fields: clearedFieldPaths,
        setHasError,
      });
    }
  }, [cyclosFields, setErrors, setHasError]);

  // ══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════ JSON & Fields Loading ═════════════════════════

  const getFields = useCallback(async (group) => {
    try {
      if (Object.keys(pincodeService?.records)?.length === 0) {
        pincodeService.fetch().catch(console.error);
      }
      await CountrySelectService.fetch();
      const cyclosResponse = await doGET(`/api/cyclos/fields-json?group=${group}`);
      if (cyclosResponse.status === 200) {
        const responseData = cyclosResponse?.data;

        // Store full JSON config for navigation/conditions
        setJsonConfig(responseData);
        setAutoSaveConfig(responseData?.autoSave || null);
      }
    } catch (error) {
      showError(error);
    }
  }, [showError]);

  useEffect(() => {
    getFields("Business"); // V2 group — NOT "BUSINESS_EDD"
    CountrySelectService.fetchCyclosCountryMaster();
  }, [getFields]);

  const documentFirstConfigRef = useRef(null);
  useEffect(() => {
    if (!isDocumentFirstShell) return;
    const loadedForm = jsonConfig?.form ?? null;
    if (loadedForm === DOCUMENT_FIRST_FORM_ID) return;
    if (documentFirstConfigRef.current === loadedForm) return;
    documentFirstConfigRef.current = loadedForm;
    getFields(DOCUMENT_FIRST_FIELDS_GROUP);
  }, [isDocumentFirstShell, jsonConfig?.form, getFields]);

  useEffect(() => { if (formId) setEditID(formId) }, [formId]);

  useEffect(() => {
    if (!Array.isArray(jsonConfig?.fields)) return;

    const resolvedFields = resolveFieldsForType(jsonConfig.fields, v2Type);

    setIPlusFields(resolvedFields);

    const customFieldObject = Object.entries(createFieldDefinitionMap(resolvedFields)).reduce((acc, [key, field]) => {
      let normalizedField = field;
      if (field.field === "individuals[].mobilePhone") {
        normalizedField = {
          ...field,
          uiType: "PhoneNumWithValidation",
          validateOnly: true,
        };
      }
      if (field.field === "mobilePhone" || field.field === "landlinePhone") {
        normalizedField = {
          ...field,
          uiType: "PhoneNumWithValidation",
          validateOnly: true,
          phoneType: field.field === "landlinePhone" ? "landLine" : field.phoneType,
        };
      }
      acc[key] = normalizedField;
      return acc;
    }, {});
    setCyclosFields(customFieldObject);
  }, [jsonConfig, v2Type]);

  useEffect(() => {
    if (editMode) {
      setStep({ wizard: WIZARD.BusinessType });
    }
  }, [editMode]);

  // ══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════ Data Fetching ═════════════════════════════════

  const fetchDetail = useCallback(async ({ stepProp, usernameProp, syncUi = true } = {}) => {
    let businessResponse = {};
    let applicationFormResponse = {};
    if (syncUi) setDataLoading(true);

    try {
      if (editId) {
        localStorage.setItem("formId", editId);

        const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`);
        if (applicationResponse?.status === 200) {
          applicationFormResponse = applicationResponse?.data;
          businessResponse = await fetchBusinessDetailData(editId);

          // Set V2 type from application form
          if (applicationFormResponse?.type) {
            setV2Type(Number(applicationFormResponse.type));
          }

          let forcedWizard = null;

          if (syncUi && applicationFormResponse?.steps?.length) {
            const askInfoStep = checkIsAskForInfo(applicationFormResponse?.steps);
            const stepsObj = convertArrayToDict(applicationFormResponse?.steps);
            setApplicationSteps(stepsObj);

            const applicationFormStepStatus = stepsObj[STEPS.APPLICATION_FORM]?.status;
            const documentVerificationStepStatus = stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status;
            const isDocumentRedoRequired = applicationFormStepStatus > STEP_STATUS.DRAFT
              && [STEP_STATUS.DRAFT, STEP_STATUS.INCOMPLETE].includes(documentVerificationStepStatus);
            const hasIncompleteExtraQuestions = hasPendingAsks(businessResponse?.asks)
              && askInfoStep?.status === STEP_STATUS.INCOMPLETE
              && askInfoStep?.label === STEPS.APPLICATION_FORM
              && applicationFormStepStatus === STEP_STATUS.INCOMPLETE;

            if (!editMode
              && documentVerificationStepStatus > STEP_STATUS.DRAFT
              && [STEP_STATUS.SUBMITTED, STEP_STATUS.APPROVED, STEP_STATUS.REJECTED].includes(documentVerificationStepStatus)
              && applicationFormStepStatus > STEP_STATUS.DRAFT
              && [STEP_STATUS.SUBMITTED, STEP_STATUS.APPROVED, STEP_STATUS.REJECTED].includes(applicationFormStepStatus)
            ) {
              forcedWizard = WIZARD.ThankYou;
            } else if (!editMode && isDocumentRedoRequired) {
              forcedWizard = WIZARD.ApplicationDocRedo;
            } else if (!editMode && hasIncompleteExtraQuestions) {
              forcedWizard = WIZARD.AskMoreMessage;
            }

          }

          setApplicationForm(applicationFormResponse);

          // ── Normalize business data ──
          businessResponse.individuals = movePrimaryUserToFront(businessResponse.individuals || []);
          if (businessResponse.individuals.length === 0) {
            const newIndividuals = [{ isPrimaryUser: true, _isNew: true, _localId: buildLocalId() }];
            businessResponse = setValue(businessResponse, 'individuals', newIndividuals, false);
          }

          // Primary user enforcement
          const primaryUser = businessResponse.individuals?.[0];
          if (_.isPlainObject(primaryUser)) {
            primaryUser.isPrimaryUser = true;
            if (!primaryUser.email) primaryUser.email = businessResponse.email;
            if (!primaryUser.accessType) primaryUser.accessType = "NoGroup";
            if (!primaryUser.isAuthorizedUser) primaryUser.isAuthorizedUser = true;
          }

          businessResponse.individuals = businessResponse.individuals.map((individual, index) => {
            const next = { ...(individual ?? {}) };
            next.isPrimaryUser = index === 0;
            if (index === 0) {
              next.isAuthorizedUser = true;
            }
            if (next.isAuthorizedUser && !next.accessType) next.accessType = "NoGroup";
            return next;
          });

          // Address normalization
          if (!businessResponse.address) businessResponse.address = {};
          if (!Array.isArray(businessResponse.addressList)) {
            businessResponse = setValue(businessResponse, 'addressList', [], false);
          }

          const hasRegistered = businessResponse.addressList.some(addr => addr.type === "REGISTERED");
          const hasMailing = businessResponse.addressList.some(addr => addr.type === "MAILING");

          if (!hasRegistered) {
            const newAddressList = [{ type: "REGISTERED" }, ...businessResponse.addressList];
            businessResponse = setValue(businessResponse, 'addressList', newAddressList, false);
          }
          if (!hasMailing) {
            const newAddressList = [...businessResponse.addressList];
            newAddressList.splice(1, 0, { type: "MAILING" });
            businessResponse = setValue(businessResponse, 'addressList', newAddressList, false);
          }

          businessResponse.addressList = businessResponse.addressList.filter(addr =>
            ["REGISTERED", "MAILING"].includes(addr.type)
          );

          // Same-as address detection
          const principalAddress = businessResponse.address;
          const registeredAddress = businessResponse.addressList.find(addr => addr.type === "REGISTERED") || {};
          const mailingAddress = businessResponse.addressList.find(addr => addr.type === "MAILING") || {};
          const compareAddresses = (addr1, addr2) => {
            const fields = ['street', 'buildingNumber', 'city', 'state', 'pinCode', 'country'];
            if (!addr1 || !addr2) return false;
            return fields.every(field => addr1[field] === addr2[field] && addr1[field] !== undefined);
          };
          businessResponse.isRegisteredAddressSame = compareAddresses(principalAddress, registeredAddress);
          businessResponse.isMailingAddressSame = compareAddresses(principalAddress, mailingAddress);

          // Pin code hide flags (self-heals a missing pinCode when the country has a fixed default)
          businessResponse.address = withComputedHidePinCode(businessResponse.address);
          businessResponse.addressList = ensureDraftArrayItemIdentity(
            businessResponse.addressList.map(addr => withComputedHidePinCode(addr)),
            ['type']
          );
          businessResponse.individuals = ensureDraftArrayItemIdentity(
            businessResponse.individuals.map(ind => ({
              ...ind,
              address: withComputedHidePinCode(ind.address)
            })),
            ['aEId', 'cyclosUserId', 'cyclosUserName', '_id']
          );

          // Entity initialization
          if (businessResponse?.hasEntity && (!Array.isArray(businessResponse.entities) || businessResponse.entities.length === 0)) {
            businessResponse.entities = [{}];
          }
          businessResponse.entities = ensureDraftArrayItemIdentity(
            businessResponse.entities || [],
            ['aEId', '_id']
          );

          const resolvedFormType = applicationFormResponse?.formType
            ?? applicationResponse?.data?.formType
            ?? businessResponse?.formType;
          const resolvedApplicationType = applicationFormResponse?.type
            ?? applicationResponse?.data?.type
            ?? businessResponse?.type;
          const formData = {
            ...businessResponse,
            cyclosUserName: usernameProp ? usernameProp : (applicationResponse?.data?.cyclosUserName ?? businessResponse?.cyclosUserName),
            formType: resolvedFormType,
            type: resolvedApplicationType,
          };

          if (syncUi) {
            const resolvedType = Number(applicationFormResponse?.type || v2TypeRef.current || V2_TYPE.PLUS);
            const normalizedStepProp = stepProp
              ? { wizard: normalizeWizardForV2(stepProp?.wizard, resolvedType) }
              : null;
            calculateInitialErrors({
              data: formData,
              step: (forcedWizard ? { wizard: forcedWizard } : normalizedStepProp) || (stepWizardRef.current ? { wizard: stepWizardRef.current } : null),
              iPlusFields,
              setErrors,
              errors: {},
              type: resolvedType,
            });
            setData(formData);

            const queryStep = new URLSearchParams(locationSearchRef.current).get('step');
            const shouldForceFirstWizard = !normalizedStepProp?.wizard
              && !forcedWizard
              && resolvedType === V2_TYPE.PRIME
              && forcePrimeFirstStepOnEntry;
            const queryWizard = forcedWizard
              ? null
              : shouldForceFirstWizard
                ? null
              : resolveWizardFromStepParam(queryStep, {
                type: resolvedType,
                formData,
                jsonConfig,
              });

            const initialWizard = forcedWizard
              || normalizedStepProp?.wizard
              || queryWizard
              || (
                jsonConfig
                  ? normalizeWizardForV2(
                    getNextStepV2("__INIT__", resolvedType, formData, jsonConfig)?.wizard,
                    resolvedType
                  )
                  : getDefaultInitialWizardForType(resolvedType, jsonConfig)
              )
              || getDefaultInitialWizardForType(resolvedType, jsonConfig);

            const gatedInitialWizard = clampWizardToDocumentGate({
              wizard: initialWizard,
              sequence: getQueryWizardSequence({ type: resolvedType, formData, jsonConfig }),
              documentFirst: isDocumentFirst({
                applicationForm: { ...applicationFormResponse, type: resolvedType },
                isBrokerMode,
                v2Type: resolvedType,
              }),
              // Honour a deep-linked step on reload once the start-extraction gate has
              // actually been passed (its terminal state is persisted). We never relax
              // the clamp while extraction is still pending, so a genuinely-closed gate
              // can't be bypassed by reloading.
              gateOpen: ['completed', 'failed'].includes(applicationFormResponse?.startExtractionState),
            });

            const documentFirstShellHere = isDocumentFirst({
              applicationForm: { ...applicationFormResponse, type: resolvedType },
              isBrokerMode,
              v2Type: resolvedType,
            });
            const stepConfigReady = !documentFirstShellHere
              || jsonConfig?.form === DOCUMENT_FIRST_FORM_ID;

            if (stepConfigReady) {
              if (gatedInitialWizard !== initialWizard) {
                deferredWizardRef.current = initialWizard;
                deferredAtRef.current = Date.now();
              }

              setStep({ wizard: gatedInitialWizard });
              const initialDisplayStep = getDisplayStepNumberForWizard(gatedInitialWizard, {
                type: resolvedType,
                formData,
                jsonConfig,
              });
              if (initialDisplayStep > 0) {
                pageNumberRef.current = initialDisplayStep;
              }
            }
          }
          return formData;
        }
      }
    } catch (error) {
      console.error("fetchDetail error:", error);
    } finally {
      if (syncUi) {
        setDataLoading(false);
        scrollToTop();
      }
    }
    return null;
  }, [editId, editMode, forcePrimeFirstStepOnEntry, iPlusFields, isBrokerMode, jsonConfig]);

  useEffect(() => {
    if (!jsonConfig) {
      return;
    }

    fetchDetail({});
  }, [editId, fetchDetail, jsonConfig, refreshId]);

  // ══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════ AutoSave ══════════════════════════════════════

  const isAutoSaveEnabled = !asLabel
    && !!editId
    && !!data?._id
    && !loading
    && !dataLoading
    && isAutoSaveEnabledForConfig({ autoSaveConfig, step: stepWizard });

  const autoSaveDebounceMs = resolveAutoSaveDebounceMs(autoSaveConfig);

  const autoSaveAdapter = useMemo(
    () => createBusinessV2AutoSaveAdapter(cyclosFields, autoSaveConfig),
    [autoSaveConfig, cyclosFields]
  );

  const {
    autoSaveStatus,
    lastSavedAt,
    pendingChangesCount,
    pendingChanges,
    hasUnsavedChanges,
    isAutoSaveNavigationPending,
    flushNow,
    flushAndPauseAutoSave,
    resumeAutoSave,
    syncAfterRefetch,
  } = useAutoSave({
    data,
    isEnabled: isAutoSaveEnabled,
    debounceMs: autoSaveDebounceMs,
    adapter: autoSaveAdapter,
    onDirtyUpdate: async () => await fetchDetail({ syncUi: false }),
    onDataReconcile: (value) => {
      setData((previousData) => {
        const nextData = typeof value === 'function' ? value(previousData) : value;
        return preserveBusinessV2RootMeta(previousData, nextData, applicationFormRef.current);
      });
    },
    onFieldPersisted: reviewNavigation?.commitFieldTask,
  });

  useUnsavedChangesWarning({
    when: hasUnsavedChanges || loading,
    isSaving: autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing',
    pendingChangesCount,
  });

  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const adoptedExtractionRunRef = useRef(null);

  const adoptServerExtraction = useCallback(async ({ runKey } = {}) => {
    if (runKey && adoptedExtractionRunRef.current === runKey) return;

    if (hasUnsavedChangesRef.current) {
      try {
        await flushNow();
      } catch (e) {
      }
    }

    const fresh = await fetchDetail({ syncUi: false });
    if (!fresh) return;
    if (runKey) adoptedExtractionRunRef.current = runKey;
    setData((previousData) => preserveBusinessV2RootMeta(previousData, fresh, applicationFormRef.current));
    syncAfterRefetch(fresh);
  }, [fetchDetail, flushNow, syncAfterRefetch]);

  const refreshApplicationFormOnly = useCallback(async () => {
    await fetchDetail({ syncUi: false });
  }, [fetchDetail]);

  const businessTypeAutoSaveValueRef = useRef();

  useEffect(() => {
    const currentBusinessType = data?.businesses ?? null;
    const previousBusinessType = businessTypeAutoSaveValueRef.current;
    businessTypeAutoSaveValueRef.current = currentBusinessType;

    if (previousBusinessType === undefined) {
      return;
    }

    if (!isAutoSaveEnabled || dataLoading || stepWizard !== WIZARD.BusinessType) {
      return;
    }

    if (_.isEqual(previousBusinessType, currentBusinessType)) {
      return;
    }

    flushNow();
  }, [data?.businesses, dataLoading, flushNow, isAutoSaveEnabled, stepWizard]);

  const nextActionLoading = loading || isAutoSaveNavigationPending;
  const currentPageReviewTasks = reviewNavigation?.currentWizardActionableTasks || [];
  const shouldGenerateBeneficialOwnersCertificate = Boolean(cyclosFields?.tnc);

  // ══════════════════════════════════════════════════════════════════════════
  // ══════════════════════════ Conversion Confirmation ═════════════════════════

  const askForConversionValueUpdate = async () => {
    const fieldsToUpdate = [];
    if (
      data?.hasCoinConversionPlan === false && (
        data?.Number_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
        data?.Amount_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
        data?.Number_Of_Monthly_Stablecoin_Conversions_Payments > 0 ||
        data?.Amount_Of_Monthly_Stablecoin_Conversions_Payments > 0
      )) {
      fieldsToUpdate.push("hasCoinConversionPlan");
    }
    if (
      data?.hasCryptoConversionPlan === false && (
        data?.Count_Of_Monthly_Cryptocurrency_Conversions > 0 || data?.Amount_Of_Monthly_Cryptocurrency_Conversions > 0
      )) {
      fieldsToUpdate.push("hasCryptoConversionPlan");
    }
    if (fieldsToUpdate.length === 0) {
      return { success: true, fieldsToUpdate: [], error: "" };
    }
    const title = fieldsToUpdate.map(field => CONVERSIONS_FIELD_MAP[field]).join(" and ");
    const response = await showConfirm({ title: t(`Do you want to update ${title} to Yes?`) });
    if (response) return { success: true, fieldsToUpdate, error: '' };
    return { success: false, fieldsToUpdate, error: `You have to update ${title} to Yes or change their values to 0 to proceed.` };
  };

  // ══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════ Update & Submit ═══════════════════════════════

  const handleUpdate = async ({ nextStep, payload, isfetchDetail, isLastStep, skipUpdate }) => {
    let errorFlag = false;
    let latestData = data;
    let didResumeAutoSave = false;

    if ((payload ?? data)?._id) {
      setLoading(true);
      setHasError(false);
      const autoSaveResult = await flushAndPauseAutoSave();
      if (!autoSaveResult?.ok) {
        setLoading(false);
        setHasError(true);
        showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
        return null;
      }
      latestData = autoSaveResult?.latestData || data;

      // Review task blocking (unchanged from periodic review pattern)
      const refreshedCurrentPageReviewTasks = reviewNavigation?.peekCurrentWizardActionableTasks?.() || currentPageReviewTasks;
      const refreshedActionableReviewTasks = reviewNavigation?.peekActionableTasks?.() || reviewNavigation?.actionableTasks || [];

      // A step reaches the final submission when it advances to the Thank-You wizard, even
      // if the step component did not pass isLastStep. Broker-sponsored apps hide the PayNow
      // and SelfDeclaration steps (the ones that set isLastStep), so for them the real last
      // step is a generic field step — gate the final submit on this, not on isLastStep,
      // otherwise the app lands on Thank-You without ever calling /api/business/submit.
      const reachesTerminal = isLastStep || nextStep?.wizard === WIZARD.ThankYou;

      if (nextStep && !reachesTerminal && !asLabel && !editMode && refreshedCurrentPageReviewTasks.length) {
        const blockedTask = reviewNavigation?.peekPreferredActionableTask?.(reviewNavigation?.currentWizard || "") || refreshedCurrentPageReviewTasks[0];
        resumeAutoSave(latestData);
        didResumeAutoSave = true;
        setLoading(false);
        showError("Please finish the remaining items on this page before continuing.");
        if (blockedTask) reviewNavigation?.goToTask(blockedTask);
        return { status: 400 };
      }
      if (reachesTerminal && !asLabel && !editMode && refreshedActionableReviewTasks.length) {
        const blockedTask = reviewNavigation?.peekPreferredActionableTask?.() || refreshedActionableReviewTasks[0];
        resumeAutoSave(latestData);
        didResumeAutoSave = true;
        setLoading(false);
        showError("Please complete all requested items before submitting.");
        if (blockedTask) reviewNavigation?.goToTask(blockedTask);
        return { status: 400 };
      }

      if (reachesTerminal && isDocumentFirstShell && !asLabel && !editMode) {
        const { hasErrors, recordLevel, fieldLevel } = runRecordValidators(latestData);
        if (hasErrors) {
          resumeAutoSave(latestData);
          didResumeAutoSave = true;
          setLoading(false);
          setHasError(true);
          showError((recordLevel[0] || fieldLevel[0])?.message
            || 'Please add the required people before submitting.');
          navigateToWizard(WIZARD.PeopleAndOwnership, { type: v2Type, formData: latestData });
          return { status: 400 };
        }
      }

      let updatedPayload = { ...buildPostAutoSaveUpdatePayload(latestData, payload) };
      let persistedNextData = null;

      // Conversion confirmation for merged Business Plus account activity step
      if (Number(v2Type) === V2_TYPE.PLUS && stepWizard === WIZARD.BusinessActivity) {
        const { success, fieldsToUpdate, error } = await askForConversionValueUpdate();
        if (!success) {
          resumeAutoSave(latestData);
          didResumeAutoSave = true;
          setLoading(false);
          showError(error);
          return { status: 400 };
        }
        fieldsToUpdate.forEach(field => { updatedPayload[field] = true; });
      }

      // Individual normalization
      if (updatedPayload.individuals?.length) {
        updatedPayload.individuals = updatedPayload.individuals.map((individual, index) => {
          const next = { ...(individual ?? {}) };
          if (index === 0) {
            next.isPrimaryUser = true;
            next.isAuthorizedUser = true;
            if (!next.email && updatedPayload.email) next.email = updatedPayload.email;
            if (!next.accessType) next.accessType = "NoGroup";
          } else if (next.isAuthorizedUser && !next.accessType) {
            next.accessType = "NoGroup";
          }
          return next;
        });
      }

      // Entity cleanup
      if (!updatedPayload.hasEntity) updatedPayload.entities = [];

      // Country code cleanup
      const keysToOmit = [];
      if (!updatedPayload.landlinePhone) keysToOmit.push('landlineCountryCode');
      if (!updatedPayload.mobilePhone) keysToOmit.push('mobileCountryCode');
      updatedPayload = _.omit(_.omitBy(updatedPayload, value => value === ""), keysToOmit);
      delete updatedPayload?.selfDeclaredValues;

      try {
        if (!asLabel && !skipUpdate) {
          const normalizedPayload = normalizeBusinessV2PayloadForApi(updatedPayload, cyclosFields);
          // Broker-sponsored applications reuse the shared business endpoints;
          // the backend authorizes broker tokens via the broker-scoped
          const response = await doPUT(`/api/business/update`, normalizedPayload);
          if (response?.status === 200) {
            const nextData = mergeBusinessV2ResponseData({
              responseData: response?.data ?? {},
              fallbackData: updatedPayload,
              formType: applicationForm?.formType,
              type: v2Type,
            });
            persistedNextData = nextData;
            setData(nextData);
            resumeAutoSave(nextData);
            didResumeAutoSave = true;
          } else {
            errorFlag = true;
            resumeAutoSave(null);
            didResumeAutoSave = true;
          }
        }

        // Final submission
        if (reachesTerminal && !asLabel && !editMode) {
          // Broker applications (identified by brokerId) are never charged, so
          // they skip the payment-driven post-submit flow and navigate straight on.
          const isSponsoredSubmit = isBrokerMode
            || Boolean(componentApplicationForm?.brokerId)
            || Boolean(data?.brokerId)
            || Boolean(updatedPayload?.brokerId);
          // Shared submit endpoint handles broker-sponsored apps: it authorizes
          // broker tokens (broker-scoped userAuthCheck) and applies the broker
          const submitResponse = await doGET(`/api/business/submit?id=${editId}&markFormSubmit=true&markDocSubmit=true`);
          if (submitResponse?.status === 200) {
            if (shouldGenerateBeneficialOwnersCertificate && !isBrokerMode) {
              try {
                await doGET(`/api/application-doc/cert/beneficial-owners?form_id=${editId}`);
              } catch (error) {
                console.log("Certificate generation error:", error);
              }
            }
            await reviewNavigation?.markVisibleTasksResponded?.({ quiet: true });
            handleUtmFormSubmit({
              formType: "BUSINESS_V2",
              utmPayload: {
                utm_source: applicationForm?.extension?.utm_source,
                utm_medium: applicationForm?.extension?.utm_medium,
                utm_campaign: applicationForm?.extension?.utm_campaign,
                utm_content: applicationForm?.extension?.utm_content,
                utm_term: applicationForm?.extension?.utm_term,
              }
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (isSponsoredSubmit) {
              pageNumberRef.current = nextStep
                ? getDisplayStepNumberForWizard(nextStep.wizard, { type: v2Type, formData: data, jsonConfig })
                : getDisplayStepNumberForWizard(WIZARD.ThankYou, { type: v2Type, formData: data, jsonConfig });
              navigateToWizard(nextStep?.wizard || WIZARD.ThankYou, {
                type: v2Type,
                formData: persistedNextData || updatedPayload,
              });
              setLoading(false);
            } else if (isfetchDetail) {
              pageNumberRef.current = nextStep
                ? getDisplayStepNumberForWizard(nextStep.wizard, { type: v2Type, formData: data, jsonConfig })
                : getDisplayStepNumberForWizard(WIZARD.BusinessInformation, { type: v2Type, formData: data, jsonConfig });
              await fetchDetail(nextStep ? { stepProp: nextStep } : {});
              setLoading(false);
            }
          } else {
            errorFlag = true;
          }
        } else {
          // Navigate to next step
          if (nextStep) {
            navigateToWizard(nextStep.wizard, {
              type: v2Type,
              formData: persistedNextData || updatedPayload,
            });
          } else {
            pageNumberRef.current = pageNumberRef.current + 1;
          }
          scrollToTop();
          setLoading(false);
        }
      } catch (error) {
        setHasError(true);
        showError(error);
        errorFlag = true;
        resumeAutoSave(null);
        didResumeAutoSave = true;
      } finally {
        if (skipUpdate && !didResumeAutoSave) resumeAutoSave(latestData);
        setLoading(false);
      }
    } else {
      if (nextStep) fetchDetail({ stepProp: nextStep });
      resumeAutoSave(null);
    }

    return errorFlag ? { status: 400 } : { status: 200 };
  };

  // ══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════ Type Switching ════════════════════════════════

  const handleTypeSwitch = async (newType, options = {}) => {
    if (newType === v2Type) return;
    if (isBrokerMode) {
      showError("Partner sponsored application type is set when the draft is created.");
      return false;
    }
    if (!isTypeSwitchAllowed(applicationForm, jsonConfig, newType)) {
      showError("Type switching is not allowed after account creation.");
      return false;
    }

    const {
      anchorWizard = stepWizardRef.current,
      navigationMode = 'preserve',
    } = options;

    setTypeSwitchLoading(true);
    setLoading(true);
    const autoSaveResult = await flushAndPauseAutoSave();
    if (!autoSaveResult?.ok) {
      setTypeSwitchLoading(false);
      setLoading(false);
      showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
      return false;
    }

    const latestData = autoSaveResult?.latestData || data;
    const cleanedData = {
      ...cleanupFieldsForTypeSwitch(latestData, v2Type, newType, jsonConfig),
      type: newType,
    };
    let didResumeTypeSwitchAutoSave = false;
    let latestResolvedData = latestData;

    try {
      const typeSwitchResponse = await doPUT('/api/business/update-type', {
        _id: applicationForm?._id || cleanedData?._id || editId,
        type: newType,
      });

      if (typeSwitchResponse?.status === 200) {
        const switchedData = mergeBusinessV2ResponseData({
          responseData: typeSwitchResponse?.data || {},
          fallbackData: {
            ...latestData,
            type: newType,
            interestedInPrime: newType === V2_TYPE.PRIME,
          },
          formType: applicationForm?.formType,
          type: newType,
        });

        const cleanedSwitchedData = {
          ...cleanupFieldsForTypeSwitch(switchedData, v2Type, newType, jsonConfig),
          type: newType,
          interestedInPrime: newType === V2_TYPE.PRIME,
        };

        latestResolvedData = cleanedSwitchedData;

        const normalizedSwitchedPayload = normalizeBusinessV2PayloadForApi(switchedData, cyclosFields);
        const normalizedCleanedPayload = normalizeBusinessV2PayloadForApi(cleanedSwitchedData, cyclosFields);

        let nextResponseData = typeSwitchResponse?.data || {};

        if (!_.isEqual(normalizedSwitchedPayload, normalizedCleanedPayload)) {
          const response = await doPUT('/api/business/update', normalizedCleanedPayload);
          if (response?.status !== 200) {
            showError("Unable to switch application type.");
            return false;
          }
          nextResponseData = response?.data || nextResponseData;
        }

        const nextData = mergeBusinessV2ResponseData({
          responseData: nextResponseData,
          fallbackData: cleanedSwitchedData,
          formType: applicationForm?.formType,
          type: newType,
        });
        setData(nextData);
        setV2Type(newType);
        setApplicationForm((prev) => ({
          ...(prev || {}),
          type: newType,
          connectedApplication: newType === V2_TYPE.PRIME,
        }));
        resumeAutoSave(nextData);
        didResumeTypeSwitchAutoSave = true;

        const targetWizard = (() => {
          const normalizedAnchorWizard = normalizeWizardForV2(anchorWizard, newType);
          const activeSequence = getQueryWizardSequence({ type: newType, formData: nextData, jsonConfig });

          if (navigationMode === 'first') {
            return activeSequence[0] || getDefaultInitialWizardForType(newType, jsonConfig);
          }

          if (navigationMode === 'next') {
            const nextWizard = getNextStepV2(normalizedAnchorWizard, newType, nextData, jsonConfig)?.wizard;
            if (nextWizard && nextWizard !== normalizedAnchorWizard) {
              return nextWizard;
            }
          }

          if (normalizedAnchorWizard && activeSequence.includes(normalizedAnchorWizard)) {
            return normalizedAnchorWizard;
          }

          return resolveFallbackWizard({
            currentWizard: normalizedAnchorWizard,
            type: newType,
            formData: nextData,
            jsonConfig,
          }) || getDefaultInitialWizardForType(newType, jsonConfig);
        })();

        navigateToWizard(targetWizard, { type: newType, formData: nextData });
        return true;
      }
      showError("Unable to switch application type.");
    } catch (error) {
      showError(error);
    } finally {
      if (!didResumeTypeSwitchAutoSave) resumeAutoSave(latestResolvedData);
      setTypeSwitchLoading(false);
      setLoading(false);
    }

    return false;
  };

  const openPrimeRequiredModal = useCallback(({ optionLabel } = {}) => new Promise((resolve) => {
    primeRequiredResolverRef.current = resolve;
    setPrimeRequiredModalState({
      isOpen: true,
      optionLabel: optionLabel || "",
    });
  }), []);

  const closePrimeRequiredModal = useCallback((confirmed) => {
    const resolver = primeRequiredResolverRef.current;
    primeRequiredResolverRef.current = null;
    setPrimeRequiredModalState({
      isOpen: false,
      optionLabel: "",
    });
    resolver?.(confirmed);
  }, []);

  const handlePrimeRequiredByBusinessType = async ({ businessTypeRule } = {}) => {
    const selectedBusinessTypeLabel = businessTypeRule?.label || "the selected business type";
    const confirmed = await openPrimeRequiredModal({ optionLabel: selectedBusinessTypeLabel });

    if (!confirmed) {
      return { status: 400 };
    }

    const switched = await handleTypeSwitch(V2_TYPE.PRIME, {
      anchorWizard: WIZARD.BusinessType,
      navigationMode: 'next',
    });
    return switched ? { status: 200 } : { status: 400 };
  };

  const handleHeaderTypeSwitch = (newType) => (
    handleTypeSwitch(newType, {
      navigationMode: 'first',
      anchorWizard: null,
    })
  );

  // ══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════ Rendering ═════════════════════════════════════

  const documentFirstConfigPending = isDocumentFirstShell
    && jsonConfig?.form !== DOCUMENT_FIRST_FORM_ID;
  const isLoading = dataLoading
    || documentFirstConfigPending
    || !Object.keys(cyclosFields)?.length
    || !CountrySelectService?.cyclosCountryMaster?.length;
  const showTypeSwitch = !asLabel
    && !editMode
    && !isBrokerMode
    && Number(v2Type) === V2_TYPE.PLUS
    && isTypeSwitchAllowed(applicationForm, jsonConfig);
  const displayStepNumber = useMemo(
    () => getDisplayStepNumberForWizard(stepWizard, { type: v2Type, formData: data, jsonConfig }),
    [data, jsonConfig, stepWizard, v2Type]
  );

  useEffect(() => {
    if (displayStepNumber > 0) {
      pageNumberRef.current = displayStepNumber;
    }
  }, [displayStepNumber]);

  const documentFirstSteps = useMemo(
    () => displayWizardSequence.map((wizardKey) => ({
      key: wizardKey,
      label: jsonConfig?.wizardLabels?.[wizardKey] || ACCORDION_LABELS[wizardKey] || wizardKey,
    })),
    [displayWizardSequence, jsonConfig]
  );
  const documentFirstClientName = data?.Business_Name
    || applicationForm?.customerName
    || applicationForm?.email
    || data?.email
    || 'Draft client';
  const documentFirstSubtitle = `${v2TypeLabel} application · ${isLocked ? 'read only' : 'draft'}`;
  const brokerApplicationsPath = useMemo(() => {
    const basePathMatch = String(location.pathname || '').match(/^(.*)\/applications\//);
    return `${basePathMatch?.[1] || '/broker'}/applications`;
  }, [location.pathname]);
  const handleSaveAndExit = useCallback(async () => {
    try {
      await flushNow?.();
    } catch (flushError) {
    }
    navigate(brokerApplicationsPath);
  }, [brokerApplicationsPath, flushNow, navigate]);

  if (!asLabel && !isLoading && !editMode && stepWizard === WIZARD.ApplicationDocRedo) {
    return (
      <div>
        <ApplicationDocRedo
          recordId={editId}
          asLabel={asLabel}
          formId={editId}
          formType={FORM_TYPE.Business}
          type={formTitle}
        />
      </div>
    );
  }

  if (!asLabel && !isLoading && !editMode && stepWizard === WIZARD.AskMoreMessage) {
    return (
      <div>
        <AskMoreMessage
          data={data}
          title={formTitle}
          remark={(data?.remark ?? "")}
          loading={nextActionLoading}
          onNext={() => { navigateToWizard(WIZARD.ExtraQuestions) }}
        />
      </div>
    );
  }

  if (!asLabel && !isLoading && !editMode && stepWizard === WIZARD.ThankYou) {
    return <ThankYou
      application_id={editId}
      type={applicationForm?.formType}
      applicationType={applicationForm?.type}
      applicationForm={applicationForm}
      formData={data}
    />;
  }

  // Console accordion rendering (asLabel mode)
  const renderAsAccordion = () => {
    const baseAccordionProps = {
      decrementStep: () => {
        const prevStep = handlePrevV2(stepWizard, v2Type, data, jsonConfig);
        navigateToWizard(prevStep?.wizard, { type: v2Type, formData: data });
      },
      recordId: editId,
      cyclosFields,
      iPlusFields,
      data,
      handleChange,
      pageNum: pageNumberRef.current,
      loading: nextActionLoading,
      step,
      wizard: stepWizard,
      handleUpdate,
      setErrors,
      setData,
      asLabel: !editMode,
      editMode: editMode,
      hasError,
      setHasError,
      applicationForm: componentApplicationForm,
      errors,
      setLoading,
      hideButtons: true,
      formType: componentApplicationForm?.formType,
      onOtpVerify: () => { fetchDetail({ usernameProp: data?.cyclosUserName }) },
      v2Type,
      jsonConfig,
    };

    // Resolve active wizards from JSON config
    const wizardSequence = jsonConfig
      ? resolveActiveWizards(v2Type, data, jsonConfig).filter(w => w !== WIZARD.Documents && w !== WIZARD.PayNow && w !== WIZARD.ThankYou)
      : [WIZARD.BusinessInformation];

    if (hasActiveAsks(data?.asks)) {
      wizardSequence.push(WIZARD.ExtraQuestions);
    }

    const uniqueWizards = wizardSequence.filter((w, i, arr) => arr.indexOf(w) === i);

    const extraQuestionsProps = {
      dataLoading: loading || dataLoading,
      formId: editId,
      formType: componentApplicationForm?.formType,
      onSubmit: async () => {
        await fetchDetail({});
        window.location.reload();
      },
      questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? [],
    };

    const steps = uniqueWizards.map((wizardKey) => {
      const Component = resolveStepComponent(wizardKey, documentFirstContext);
      if (!Component) return null;

      const label = ACCORDION_LABELS[wizardKey] || jsonConfig?.wizardLabels?.[wizardKey] || wizardKey;
      const sharedProps = { ...baseAccordionProps, wizard: wizardKey, step: { wizard: wizardKey }, label };

      if (wizardKey === WIZARD.ExtraQuestions) {
        return <Component key={wizardKey} {...sharedProps} {...extraQuestionsProps} />;
      }
      return <Component key={wizardKey} {...sharedProps} />;
    }).filter(Boolean);

    return <ConsoleAccordion steps={steps} />;
  };

  const CurrentComponent = resolveStepComponent(stepWizard, documentFirstContext);
  const hasVisibleReviewTasks = Boolean(reviewNavigation?.hasVisibleTasks);

  const currentComponentDisabled = () => {
    if (isLocked) return true;
    if (![
      WIZARD.ExtraQuestions,
      WIZARD.ApplicationDocRedo,
      WIZARD.Documents,
      WIZARD.ThankYou,
    ].includes(stepWizard)) {
      return asLabel || ([1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status) && !editMode);
    }
    return asLabel;
  };

  const getPropsForCurrentComponent = () => {
    const commonProps = {
      asLabel: currentComponentDisabled(),
      hideButtons: asLabel,
      data,
      handleUpdate,
      setHasError,
      handlePrev: () => {
        const prevStep = handlePrevV2(stepWizard, v2Type, data, jsonConfig);
        navigateToWizard(prevStep?.wizard, { type: v2Type, formData: data });
      },
      formType: componentApplicationForm?.formType,
      applicationForm: componentApplicationForm,
      formId: editId || data?._id,
      loading: nextActionLoading,
      editMode,
      pageNum: displayStepNumber,
      v2Type,
      jsonConfig,
    };

    if (stepWizard === PLUS_INTRO_WIZARD) {
      const nextWizard = activeWizardSequence.find((wizardKey) => wizardKey !== PLUS_INTRO_WIZARD) || WIZARD.BusinessType;
      return {
        asLabel: currentComponentDisabled(),
        hideButtons: asLabel,
        loading: nextActionLoading,
        v2Type,
        onNext: () => {
          navigateToWizard(nextWizard, { type: v2Type, formData: data });
        },
      };
    }

    if ([
      ...BUSINESS_INFORMATION_WIZARDS, WIZARD.BusinessActivity,
      WIZARD.SelfDeclaration, WIZARD.BusinessType,
      WIZARD.AccountActivity, WIZARD.MoneyServicesBusiness,
      WIZARD.AssetsInvolvement, WIZARD.FundFamilyOffices,
      WIZARD.ComplianceDetails,
    ].includes(stepWizard)) {
      return {
        ...commonProps,
        hasError,
        setLoading,
        cyclosFields,
        errors,
        setErrors,
        iPlusFields,
        wizard: stepWizard,
        handleChange,
        setData,
        applicationForm: componentApplicationForm,
        // Authorized-user caps are JSON-configurable (broker-sponsored apps allow
        // fewer); fall back to the historical 1 / 11 limits when config is absent.
        maxAuthorizedUsers: isBrokerMode
          ? (jsonConfig?.brokerMaxAuthorizedUsers ?? 1)
          : (jsonConfig?.maxAuthorizedUsers ?? 11),
        onOtpVerify: () => { fetchDetail({ usernameProp: data?.cyclosUserName }) },
        onPrimeRequired: handlePrimeRequiredByBusinessType,
        isBrokerMode,
        ...((BUSINESS_INFORMATION_WIZARDS.includes(stepWizard)
          || DOC_FIRST_THEMED_WIZARDS.includes(stepWizard)) && isDocumentFirst(documentFirstContext)
          ? {
            variant: VARIANT.V2,
            stepCount: displayWizardSequence.length,
            stepLabel: jsonConfig?.wizardLabels?.[stepWizard]
              || ACCORDION_LABELS[stepWizard],
            // Lets the step refetch a person's required documents once their change has
            // autosaved — the backend derives the list from the stored data.
            lastSavedAt,
          }
          : {}),
      };
    }

    switch (stepWizard) {
      case WIZARD.Documents:
        return {
          ...commonProps,
          editId,
          recordId: editId,
          wizard: stepWizard,
          applicationForm: componentApplicationForm,
          pageNum: displayStepNumber,
          v2Type,
          jsonConfig,
          ...(isDocumentFirst(documentFirstContext)
            ? {
              onRefetchApplication: adoptServerExtraction,
              onPollApplication: refreshApplicationFormOnly,
              onGateChange: (next) => {
                documentGateRef.current = next;
                const deferred = deferredWizardRef.current;
                if (!deferred || !next?.open) return;
                if (Date.now() - deferredAtRef.current > DEFERRED_STEP_WINDOW_MS) {
                  deferredWizardRef.current = null;
                  return;
                }
                deferredWizardRef.current = null;
                navigateToWizard(deferred);
              },
              variant: VARIANT.V2,
              stepCount: displayWizardSequence.length,
              stepLabel: jsonConfig?.wizardLabels?.[WIZARD.Documents]
                || ACCORDION_LABELS[WIZARD.Documents],
            }
            : {}),
        };
      case WIZARD.PayNow:
        return {
          data,
          editId,
          applicationForm: componentApplicationForm,
          cyclosFields,
          handleUpdate,
          handlePrev: commonProps.handlePrev,
          setData,
          setHasError,
          loading: nextActionLoading,
          asLabel: currentComponentDisabled(),
          wizard: stepWizard,
          pageNum: displayStepNumber,
          v2Type,
          jsonConfig,
          hideButtons: asLabel,
        };
      case WIZARD.ExtraQuestions:
        return {
          dataLoading: nextActionLoading || dataLoading,
          formType: componentApplicationForm?.formType,
          data,
          asLabel: currentComponentDisabled(),
          questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? [],
          formId: editId,
          onSubmit: async () => {
            await fetchDetail({});
            window.location.reload();
          }
        };
      case WIZARD.ApplicationDocRedo:
        return {
          type: formTitle,
          formType: componentApplicationForm?.formType,
          formId: editId,
          asLabel: currentComponentDisabled(),
          recordId: editId
        };
      case WIZARD.ThankYou:
        return {
          application_id: data?._id,
          type: componentApplicationForm?.formType,
        };
      default:
        break;
    }
  };

  if (!asLabel && !isLoading && !editMode && stepWizard === WIZARD.ExtraQuestions) {
    return (
      <FormContainer
        asLabel={asLabel}
        editMode={editMode}
        form_id={data?._id}
        formContainerType={isEddApplication ? FORM_CONTAINER_TYPE.EDD : undefined}
        title={formTitle}
        isLoading={isLoading}
        showTypeSwitch={showTypeSwitch}
        currentType={Number(v2Type)}
        onSwitchType={handleHeaderTypeSwitch}
        typeSwitchDisabled={nextActionLoading}
        typeSwitchLoading={typeSwitchLoading}
        applicationForm={applicationForm}
        applicationFormId={editId}
        formData={data}
        onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
        hideLogout={isBrokerMode}
      >
        <div className='form-content-body-content flex-1' data-testid={`business-v2-shell-type-${Number(v2Type) || 'unknown'}`}>
          <div className='d-flex justify-content-end mb-3' data-testid='business-v2-autosave-indicator'>
            <AutoSaveIndicator
              status={autoSaveStatus}
              lastSavedAt={lastSavedAt}
              pendingChangesCount={pendingChangesCount}
              pendingChanges={pendingChanges}
            />
          </div>
          <ReviewShellBoundary
            reviewNavigation={reviewNavigation}
            loading={reviewLoading}
            error={reviewError}
            showReviewWizard={false}
          >
            <ExtraQuestions {...getPropsForCurrentComponent()} />
          </ReviewShellBoundary>
        </div>
      </FormContainer>
    );
  }

  // ── Main Render ──
  return (
    <FormContainer
      asLabel={asLabel}
      editMode={editMode}
      form_id={data?._id}
      formContainerType={isEddApplication ? FORM_CONTAINER_TYPE.EDD : undefined}
      title={formTitle}
      isLoading={isLoading}
      showTypeSwitch={showTypeSwitch}
      currentType={Number(v2Type)}
      onSwitchType={handleHeaderTypeSwitch}
      typeSwitchDisabled={nextActionLoading}
      typeSwitchLoading={typeSwitchLoading}
      applicationForm={applicationForm}
      applicationFormId={editId}
      formData={data}
      onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
      hideLogout={isBrokerMode}
    >
      <div className='form-content-body-content flex-1' data-testid={`business-v2-shell-type-${Number(v2Type) || 'unknown'}`}>
        <div className='d-flex justify-content-end mb-3' data-testid='business-v2-autosave-indicator'>
          <AutoSaveIndicator
            status={autoSaveStatus}
            lastSavedAt={lastSavedAt}
            pendingChangesCount={pendingChangesCount}
            pendingChanges={pendingChanges}
          />
        </div>
        {asLabel ? renderAsAccordion() : (
          <ReviewShellBoundary
            reviewNavigation={reviewNavigation}
            loading={reviewLoading}
            error={reviewError}
            showReviewWizard={hasVisibleReviewTasks && ![
              WIZARD.ThankYou,
              WIZARD.AskMoreMessage,
              WIZARD.ExtraQuestions,
              WIZARD.ApplicationDocRedo,
            ].includes(stepWizard)}
          >
            {CurrentComponent ? (
              isDocumentFirstShell ? (
                <DocumentFirstChrome
                  clientName={documentFirstClientName}
                  subtitle={documentFirstSubtitle}
                  steps={documentFirstSteps}
                  activeStep={displayStepNumber}
                  autoSaveStatus={autoSaveStatus}
                  pendingChangesCount={pendingChangesCount}
                  onSaveAndExit={handleSaveAndExit}
                  variant={VARIANT.V2}
                >
                  <CurrentComponent {...getPropsForCurrentComponent()} />
                </DocumentFirstChrome>
              ) : shouldRenderStepper ? (
                <Stepper variant='dark' activeTab={displayStepNumber || 1}>
                  {displayWizardSequence.map((wizardKey) => (
                    <div
                      key={wizardKey}
                      label={ACCORDION_LABELS[wizardKey] || jsonConfig?.wizardLabels?.[wizardKey] || wizardKey}
                    >
                      {wizardKey === stepWizard ? <CurrentComponent {...getPropsForCurrentComponent()} /> : null}
                    </div>
                  ))}
                </Stepper>
              ) : (
                <CurrentComponent {...getPropsForCurrentComponent()} />
              )
            ) : null}
          </ReviewShellBoundary>
        )}
        <PrimeRequiredModalV2
          isOpen={primeRequiredModalState.isOpen}
          optionLabel={primeRequiredModalState.optionLabel}
          brokerMode={isBrokerMode}
          onCancel={() => closePrimeRequiredModal(false)}
          onConfirm={() => closePrimeRequiredModal(true)}
        />
      </div>
    </FormContainer>
  );
};

export default observer(ApplyBusinessV2);
